import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";

// Google "G" logo — inline SVG so we don't have to fetch it.
function GoogleG(props) {
  return (
    <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden {...props}>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

export default function AuthDialog({ open, onOpenChange }) {
  const { login, register, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      toast.success(mode === "login" ? "welcome back." : "your shelf awaits.");
      onOpenChange(false);
      setEmail(""); setPassword(""); setName("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-border" data-testid="auth-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl lowercase tracking-tight">
            {mode === "login" ? "step inside." : "start your shelf."}
          </DialogTitle>
          <DialogDescription className="font-ui text-sm text-muted-foreground">
            {mode === "login"
              ? "sign in to save your reading progress and bookmarks."
              : "create an account to build a private library."}
          </DialogDescription>
        </DialogHeader>

        {/* Google */}
        <button
          type="button"
          onClick={loginWithGoogle}
          data-testid="google-signin-btn"
          className="w-full flex items-center justify-center gap-3 border border-border rounded-full px-5 py-3 font-ui text-sm hover:bg-secondary transition-colors"
        >
          <GoogleG /> continue with google
        </button>

        <div className="flex items-center gap-3 my-1 text-muted-foreground">
          <div className="h-px bg-border flex-1" />
          <span className="font-ui text-[10px] tracking-caps">or with email</span>
          <div className="h-px bg-border flex-1" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <div>
              <label className="font-ui text-xs tracking-caps text-muted-foreground">display name</label>
              <Input
                data-testid="auth-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="how you'd like to be known"
                className="mt-1"
              />
            </div>
          )}
          <div>
            <label className="font-ui text-xs tracking-caps text-muted-foreground">email</label>
            <Input
              data-testid="auth-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="reader@library.co"
              required
              className="mt-1"
            />
          </div>
          <div>
            <label className="font-ui text-xs tracking-caps text-muted-foreground">password</label>
            <Input
              data-testid="auth-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="at least 6 characters"
              required
              minLength={6}
              className="mt-1"
            />
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="auth-submit-btn"
            className="w-full rounded-full font-ui"
          >
            {busy ? "…" : mode === "login" ? "sign in" : "create account"}
          </Button>

          <button
            type="button"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            data-testid="auth-toggle-mode-btn"
            className="w-full text-center font-ui text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            {mode === "login" ? "no account yet? register instead." : "already a reader? sign in."}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
